/* =========================================================
   The metadata-driven form control.

   Renders one field according to its declared state. Colour is
   never the only cue: a lateral bar, a state icon, a tooltip and
   an aria-label all carry it (AA). Focus in edit mode tints the
   whole row (see .field-row:focus-within).
   ========================================================= */
import { useState } from 'react';
import type { FieldDef } from '@/types';
import { sized } from '@/components/Icons';
import { COUNTRIES } from '@/data/catalogs';
import { useI18n } from '@/i18n';
import { STATE, validateField } from '@/lib/fields';

export interface FieldRowProps {
  field: FieldDef;
  value: string | undefined;
  onChange?: (key: string, value: string) => void;
  editing?: boolean;
}

export function FieldRow({ field, value, onChange, editing }: FieldRowProps) {
  const { lang, t } = useI18n();
  const st = STATE[field.state];
  const label = lang === 'es' ? field.label_es : field.label_en;
  const isReadonly = field.state === 'readonly' || field.state === 'blocked' || !editing;
  const isDimmed = field.state === 'blocked';

  const [touched, setTouched] = useState(false);
  const err = touched || (field.state === 'required' && !value) ? validateField(field, value) : null;

  let displayValue = value ?? '';
  if (field.kind === 'country' && displayValue) {
    const c = COUNTRIES.find((x) => x.code === displayValue);
    if (c) displayValue = lang === 'es' ? c.name_es : c.name_en;
  }

  const stateLabel = t(`field.state_${field.state}`);
  const stateTipKey = { editable: 'edit', readonly: 'read', required: 'req', blocked: 'lock' }[field.state];
  const stateTip = t(`field.state_tip_${stateTipKey}`);

  return (
    <div
      className="field-row"
      style={{
        display: 'grid', gridTemplateColumns: '4px 1fr', gap: 10,
        background: 'var(--surface)', borderRadius: 6, padding: '8px 10px 8px 0',
        opacity: isDimmed ? 0.65 : 1,
        position: 'relative',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {/* lateral colour bar */}
      <div aria-hidden="true" style={{ background: st.color, borderRadius: '4px 0 0 4px', marginLeft: 0 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-2)',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
          }}
        >
          <span data-tip={`${stateLabel} — ${stateTip}`} style={{ color: st.color, display: 'inline-flex', alignItems: 'center' }} aria-label={stateLabel}>
            {sized(st.icon, 11, 2)}
          </span>
          <span className="truncate">{label}</span>
          {field.state === 'required' && !value ? (
            <span className="chip err" style={{ padding: '1px 6px', fontSize: 9 }}>•</span>
          ) : null}
        </div>

        {isReadonly ? (
          <div
            className={field.kind === 'mono' ? 'mono' : ''}
            style={{
              color: displayValue ? 'var(--text)' : 'var(--text-3)',
              fontSize: 13.5, fontWeight: 500, padding: '3px 0', userSelect: 'text',
            }}
          >
            {displayValue || '—'}
          </div>
        ) : (
          <input
            type={field.kind === 'number' ? 'number' : 'text'}
            value={value ?? ''}
            onChange={(e) => onChange?.(field.key, e.target.value)}
            onBlur={() => setTouched(true)}
            aria-label={label}
            aria-invalid={err ? true : undefined}
            className={field.kind === 'mono' ? 'mono' : ''}
            style={{
              border: '1px solid ' + (err ? 'var(--err)' : 'var(--line-2)'),
              background: 'var(--surface)',
              borderRadius: 5,
              padding: '6px 8px', fontSize: 13.5,
              color: 'var(--text)', outline: 'none',
              width: '100%',
            }}
          />
        )}
        {err ? <div style={{ color: 'var(--err)', fontSize: 11, fontWeight: 500 }}>{t(`field.${err}`)}</div> : null}
      </div>
    </div>
  );
}
